<template>
    <div>
        <validation-observer v-slot="{ handleSubmit }" slim>
            <form ref="paymentForm" method="post" :action="paymentSubmissionResult.paymentGatewayURL">
                <input type="hidden" name="chargetotal" :value="totalAmount.toFixed(2)" />
                <input type="hidden" name="currency" :value="paymentSubmissionResult.currency" />
                <input type="hidden" name="hash_algorithm" :value="paymentSubmissionResult.hashAlgorithm" />
                <input type="hidden" name="oid" :value="paymentReference" />
                <input type="hidden" name="responseFailURL" :value="paymentSubmissionResult.responseFailURL" />
                <input type="hidden" name="responseSuccessURL" :value="paymentSubmissionResult.responseSuccessURL" />
                <input type="hidden" name="storename" :value="paymentSubmissionResult.storeName" />
                <input type="hidden" name="timezone" :value="paymentSubmissionResult.timeZoneId" />
                <input type="hidden" name="txndatetime" :value="paymentSubmissionResult.transactionDateTime" />
                <input type="hidden" name="txntype" value="sale" />
                <input type="hidden" name="hashExtended" :value="paymentSubmissionResult.hash" />
                <div class="row">
                    <div class="col-md-3">
                        &nbsp;
                    </div>
                    <div class="col-md-6">
                        <div class="card mb-md-1">
                            <div class="card-header">
                                <h5 class="card-title">Payment Details</h5>
                            </div>
                            <div class="card-body">
                                <b-row>
                                    <b-col md="6" class="font-weight-bold">Payment Reference:</b-col>
                                    <b-col md="6" class="text-right">{{ paymentReference }}</b-col>
                                </b-row>
                                <template v-if="bankCharges || (gstAmount && !amountIncludesGst)">
                                    <b-row class="mt-2">
                                        <b-col md="6" class="font-weight-bold">Subtotal:</b-col>
                                        <b-col md="6" class="text-right">{{ formatAsCurrency(amount, currencyCode) }}</b-col>
                                    </b-row>
                                    <b-row v-if="bankCharges">
                                        <b-col md="6" class="font-weight-bold">Bank Charges:</b-col>
                                        <b-col md="6" class="text-right">{{ formatAsCurrency(bankCharges, currencyCode) }}</b-col>
                                    </b-row>
                                    <b-row v-if="gstAmount && !amountIncludesGst">
                                        <b-col md="6" class="font-weight-bold">{{ $t("Shared.Label.GST") }} ({{ formatAsPercentage(gstPercentage, true) }}):</b-col>
                                        <b-col md="6" class="text-right">{{ formatAsCurrency(gstAmount, currencyCode) }}</b-col>
                                    </b-row>
                                    <b-row class="mt-1 border-top">
                                        <b-col md="6" class="font-weight-bold">Total Amount:</b-col>
                                        <b-col md="6" class="text-right font-weight-bold">{{ formatAsCurrency(totalAmount, currencyCode) }}</b-col>
                                    </b-row>
                                    <b-row v-if="gstAmount && amountIncludesGst">
                                        <b-col class="font-weight-light">The total amount includes {{ formatAsPercentage(gstPercentage, true) }} {{ $t("Shared.Label.GST") }} which is {{ formatAsCurrency(gstAmount, currencyCode) }}</b-col>
                                    </b-row>
                                </template>
                                <template v-else>
                                    <b-row class="mt-2">
                                        <b-col md="6" class="font-weight-bold">Amount:</b-col>
                                        <b-col md="6" class="text-right font-weight-bold">{{ formatAsCurrency(totalAmount, currencyCode) }}</b-col>
                                    </b-row>
                                    <b-row v-if="gstAmount && amountIncludesGst">
                                        <b-col class="font-weight-light">The amount includes {{ formatAsPercentage(gstPercentage, true) }} {{ $t("Shared.Label.GST") }} which is {{ formatAsCurrency(gstAmount, currencyCode) }}</b-col>
                                    </b-row>
                                </template>
                            </div>
                        </div>
                        <div class="card">
                            <div class="card-header">
                                <h5 class="card-title">Credit Card Details</h5>
                            </div>
                            <div class="card-body">
                                <div class="form-row">
                                    <validation-provider ref="creditCardNumberValidationProvider" rules="required|digits_between:14,19" name="Credit Card Number" v-slot="{ errors }" slim mode="lazy">
                                        <b-form-group label="Credit Card Number"
                                                      label-for="cardnumber"
                                                      class="col-md-8">
                                            <b-form-input id="cardnumber"
                                                          name="cardnumber"
                                                          v-model="paymentDetails.creditCardNumber"
                                                          :state="errors[0] ? false : null">
                                            </b-form-input>
                                            <b-form-invalid-feedback :state="errors[0] ? false : null">{{ errors[0] }}</b-form-invalid-feedback>
                                        </b-form-group>
                                    </validation-provider>
                                    <validation-provider ref="creditCardCVVValidationProvider" rules="required|digits_between:3,4" name="CVV" v-slot="{ errors }" slim mode="lazy">
                                        <b-form-group label="CVV"
                                                      label-for="cvm"
                                                      class="col-md-4">
                                            <sensitive-input id="cvm"
                                                          name="cvm"
                                                          v-model="paymentDetails.creditCardCVV"
                                                          :state="errors[0] ? false : null">
                                            </sensitive-input>
                                            <b-form-invalid-feedback :state="errors[0] ? false : null">{{ errors[0] }}</b-form-invalid-feedback>
                                        </b-form-group>
                                    </validation-provider>
                                </div>
                                <div class="form-row">
                                    <validation-provider ref="expiryMonthValidationProvider" rules="required" name="Expiry Month" v-slot="{ errors }" slim>
                                        <b-form-group label="Expiry Month"
                                                      label-for="expmonth"
                                                      class="col-md-6">
                                            <b-form-select id="expmonth"
                                                           name="expmonth"
                                                           v-model="paymentDetails.expiryMonth"
                                                           :options="expiryMonthList"
                                                           :state="errors[0] ? false : null">
                                            </b-form-select>
                                            <b-form-invalid-feedback :state="errors[0] ? false : null">{{ errors[0] }}</b-form-invalid-feedback>
                                        </b-form-group>
                                    </validation-provider>
                                    <validation-provider ref="expiryYearValidationProvider" rules="required" name="Expiry Year" v-slot="{ errors }" slim>
                                        <b-form-group label="Expiry Year"
                                                      label-for="expyear"
                                                      class="col-md-6">
                                            <b-form-select id="expyear"
                                                           name="expyear"
                                                           v-model="paymentDetails.expiryYear"
                                                           :options="expiryYearList"
                                                           :state="errors[0] ? false : null">
                                            </b-form-select>
                                            <b-form-invalid-feedback :state="errors[0] ? false : null">{{ errors[0] }}</b-form-invalid-feedback>
                                        </b-form-group>
                                    </validation-provider>
                                </div>
                            </div>
                        </div>
                        <div class="btn-toolbar justify-content-end mt-2" role="toolbar" aria-label="Action buttons">
                            <div class="clearfix">
                                <b-button class="float-right" variant="success" @click="handleSubmit(submitPayment);">Pay</b-button>
                            </div>
                        </div>
                        <div class="form-row mt-2">
                            <div class="col-md-12">
                                For more information, please contact Epiq on {{ contactPhone }} and quote the Payment Reference: {{ paymentReference }}.
                            </div>
                        </div>
                        <div class="mb-2">
                            We accept VISA, Mastercard, American Express and CUPS.
                        </div>
                        <div class="form-row mt-2" v-if="termsAndConditionsLinkUrl">
                            <div class="col-md-12">
                                <a :href="termsAndConditionsLinkUrl" target="_blank">{{ (termsAndConditionsLinkText && (termsAndConditionsLinkText.length > 0)) ? termsAndConditionsLinkText : termsAndConditionsLinkUrl }}</a>
                            </div>
                        </div>
                    </div>
                </div>
            </form>
        </validation-observer>
    </div>

</template>

<script>
    import Vue from 'vue';
    import BootstrapVue from 'bootstrap-vue';
    import axios from 'axios';

    import { ValidationProvider, ValidationObserver } from 'vee-validate';
    import { extend, localize, validate } from 'vee-validate';
    import { required, integer, between, min, max, numeric } from 'vee-validate/dist/rules';
    import en from 'vee-validate/dist/locale/en.json';
    extend('required', required);
    extend('integer', integer);
    extend('between', between);
    extend('min', min);
    extend('max', max);
    extend('numeric', numeric);

    extend('digits_between', {
        async validate(value, { min, max }) {
            const res = await validate(value, `numeric|min:${min}|max:${max}`,)
            return res.valid;
        },
        params: ['min', 'max'],
        message: 'The {_field_} must be between {min} and {max} digits'
    });

    localize('en', en);

    import ErrorsMixin from '../../mixins/ErrorsMixin';
    import NumbersMixin from '../../mixins/NumbersMixin';
    import SensitiveInput from '../common/SensitiveInput';

    Vue.use(BootstrapVue);

    Vue.component('validation-provider', ValidationProvider);
    Vue.component('validation-observer', ValidationObserver);
    Vue.component('sensitive-input', SensitiveInput);

    export default {
        name: 'FiservDirectPost',
        mixins: [ErrorsMixin, NumbersMixin],
        props: {
            apiBaseUrl: {
                type: String,
                required: true
            },
            paymentReference: {
                type: String,
                required: true
            },
            paymentSubmissionId: {
                type: String,
                required: true
            },
            amount: {
                type: Number,
                required: true
            },
            bankCharges: {
                type: Number,
                default: null
            },
            amountIncludesGst: {
                type: Boolean,
                require: true
            },
            gstPercentage: {
                type: Number,
                default: null
            },
            gstAmount: {
                type: Number,
                default: null
            },
            totalAmount: {
                type: Number,
                required: true
            },
            currencyCode: {
                type: String,
                required: true
            },
            contactPhone: {
                type: String,
                required: true
            },
            termsAndConditionsLinkUrl: {
                type: String,
                required: true
            },
            termsAndConditionsLinkText: {
                type: String,
                required: true
            }
        },
        data: function () {
            return {
                paymentDetails: {
                    creditCardNumber: "",

                    //4147463011110083 Fully Authenticated
                    //4147463011110091 Not Authenticated
                    //4147463011110117 Attempted Authentication
                    //4147463011110042 Rejected Authentication
                    //4147463011110067 Unable to Authenticate

                    //4111 1111 1111 1111 for Visa
                    //5431 1111 1111 1111 for MasterCard
                    //3711 1111 1111 114 for Amex
                    //3600 0000 0000 08 for Diners

                    creditCardCVV: "",
                    expiryMonth: "",
                    expiryYear: ""
                },

                paymentSubmissionResult: {
                    currency: null,
                    hashAlgorithm: null,
                    paymentGatewayURL: null,
                    responseFailURL: null,
                    responseSuccessURL: null,
                    storeName: null,
                    timeZoneId: null,
                    transactionDateTime: null,
                    hash: null,
                },
                paymentSubmissionResultValid: false,

                expiryMonthList: [
                    { value: 1, text: 'January' },
                    { value: 2, text: 'February' },
                    { value: 3, text: 'March' },
                    { value: 4, text: 'April' },
                    { value: 5, text: 'May' },
                    { value: 6, text: 'June' },
                    { value: 7, text: 'July' },
                    { value: 8, text: 'August' },
                    { value: 9, text: 'September' },
                    { value: 10, text: 'October' },
                    { value: 11, text: 'November' },
                    { value: 12, text: 'December' }
                ]
            };
        },
        computed: {
            expiryYearList: function () {
                var firstYear = new Date().getFullYear();
                var yearsList = [];
                for (var i = 0; i < 10; i++) {
                    yearsList.push((firstYear + i).toString());
                }
                return yearsList;
            }
        },
        methods: {
            submitPayment: function () {
                var url = this.apiBaseUrl;
                if (!url.endsWith('/')) url += '/';
                url += "payment/fiservsubmit/" + this.paymentSubmissionId; //controller method for payment

                axios
                    .post(url, JSON.stringify(this.paymentDetails),
                        {
                            headers: {
                                'Content-Type': 'application/json'
                            }
                        })
                    .then(response => {
                        //var quote = response.data;
                        console.log(response);

                        this.paymentSubmissionResult = response.data;
                        this.paymentSubmissionResultValid = true;
                        //the Vue.nextTick will wait until the browser DOM has been updated with the new values that we just got from the API
                        //and then it will actually submit the form to the payment gateway
                        Vue.nextTick()
                            .then(() => {
                                this.$refs.paymentForm.submit();
                            });
                    })
                    .catch(ex => {
                        var currentErrors = this.handleAxiosException(ex);
                        this.showErrorsToast(currentErrors);
                    });
            }
        }
    }
</script>